/**
 * useFilterParams Hook
 *
 * Manages filter state with URL synchronization.
 * Reads filter values from URL params on mount and updates URL on changes.
 * Supports debouncing for search inputs to prevent URL spam.
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// ============================================================================
// Types
// ============================================================================

export type FilterType = 'string' | 'boolean' | 'array' | 'number';

export interface FilterSchema {
  [key: string]: {
    type: FilterType;
    default?: unknown;
    /** Debounce delay in ms (typically for search inputs) */
    debounce?: number;
    /** Custom URL param name if different from key */
    urlKey?: string;
    /** Whether to persist to URL (default: true) */
    persistToUrl?: boolean;
  };
}

export interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
  value: unknown;
}

export interface UseFilterParamsOptions<T extends Record<string, unknown>> {
  schema: FilterSchema;
  /** Labels for display in active filter chips */
  labels?: Partial<Record<keyof T, string>>;
  /** Value formatters for display in active filter chips */
  formatters?: Partial<Record<keyof T, (value: unknown) => string>>;
  /** Callback when filters change */
  onChange?: (filters: T) => void;
}

export interface UseFilterParamsReturn<T extends Record<string, unknown>> {
  /** Current filter values */
  filters: T;
  /** Set a single filter value */
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Set multiple filter values at once */
  setFilters: (filters: Partial<T>) => void;
  /** Clear a single filter */
  clearFilter: (key: keyof T) => void;
  /** Clear all filters to defaults */
  clearAllFilters: () => void;
  /** List of active (non-default) filters for display */
  activeFilters: ActiveFilter[];
  /** Whether any non-default filters are active */
  hasActiveFilters: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

function parseUrlValue(value: string | null, type: FilterType): unknown {
  if (value === null || value === '') return undefined;

  switch (type) {
    case 'boolean':
      return value === 'true';
    case 'number':
      return Number(value);
    case 'array':
      return value.split(',').filter(Boolean);
    case 'string':
    default:
      return value;
  }
}

function serializeValue(value: unknown, type: FilterType): string | null {
  if (value === undefined || value === null || value === '') return null;

  switch (type) {
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      return String(value);
    case 'array':
      return Array.isArray(value) && value.length > 0 ? value.join(',') : null;
    case 'string':
    default:
      return String(value);
  }
}

function getDefaultFilters<T extends Record<string, unknown>>(schema: FilterSchema): T {
  const defaults: Record<string, unknown> = {};
  for (const [key, config] of Object.entries(schema)) {
    defaults[key] = config.default ?? (config.type === 'array' ? [] : undefined);
  }
  return defaults as T;
}

function isDefaultValue(value: unknown, defaultValue: unknown, type: FilterType): boolean {
  if (type === 'array') {
    const arr = value as unknown[];
    const defaultArr = (defaultValue as unknown[]) ?? [];
    return arr.length === 0 || JSON.stringify(arr) === JSON.stringify(defaultArr);
  }
  return value === defaultValue || value === undefined || value === null || value === '';
}

// ============================================================================
// Hook
// ============================================================================

export function useFilterParams<T extends Record<string, unknown>>(
  options: UseFilterParamsOptions<T>
): UseFilterParamsReturn<T> {
  const { schema, labels = {}, formatters = {}, onChange } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Get defaults from schema
  const defaults = useMemo(() => getDefaultFilters<T>(schema), [schema]);

  // Initialize filters from URL params
  const getFiltersFromUrl = useCallback((): T => {
    const filters = { ...defaults };
    for (const [key, config] of Object.entries(schema)) {
      const urlKey = config.urlKey ?? key;
      const urlValue = searchParams.get(urlKey);
      const parsedValue = parseUrlValue(urlValue, config.type);
      if (parsedValue !== undefined) {
        (filters as Record<string, unknown>)[key] = parsedValue;
      }
    }
    return filters as T;
  }, [searchParams, schema, defaults]);

  const [filters, setFiltersState] = useState<T>(getFiltersFromUrl);

  // Debounce timers for each field
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  // Update URL when filters change
  const updateUrl = useCallback(
    (newFilters: T) => {
      const newParams = new URLSearchParams(searchParams);

      for (const [key, config] of Object.entries(schema)) {
        if (config.persistToUrl === false) continue;

        const urlKey = config.urlKey ?? key;
        const value = newFilters[key as keyof T];
        const serialized = serializeValue(value, config.type);

        if (serialized && !isDefaultValue(value, config.default, config.type)) {
          newParams.set(urlKey, serialized);
        } else {
          newParams.delete(urlKey);
        }
      }

      setSearchParams(newParams, { replace: true });
    },
    [schema, searchParams, setSearchParams]
  );

  // Set a single filter value
  const setFilter = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      const config = schema[key as string];
      if (!config) return;

      // Handle debouncing
      if (config.debounce && config.debounce > 0) {
        if (debounceTimers.current[key as string]) {
          clearTimeout(debounceTimers.current[key as string]);
        }
        // Update local state immediately for responsive UI
        setFiltersState((prev) => ({ ...prev, [key]: value }));
        // Debounce URL update
        debounceTimers.current[key as string] = setTimeout(() => {
          setFiltersState((prev) => {
            // Schedule URL update outside of setState to avoid React warning
            queueMicrotask(() => {
              updateUrl(prev);
              onChange?.(prev);
            });
            return prev;
          });
        }, config.debounce);
      } else {
        // Update state and schedule URL update outside of setState
        setFiltersState((prev) => {
          const newFilters = { ...prev, [key]: value };
          queueMicrotask(() => {
            updateUrl(newFilters);
            onChange?.(newFilters);
          });
          return newFilters;
        });
      }
    },
    [schema, updateUrl, onChange]
  );

  // Set multiple filters at once
  const setFilters = useCallback(
    (newFilters: Partial<T>) => {
      setFiltersState((prev) => {
        const updated = { ...prev, ...newFilters };
        queueMicrotask(() => {
          updateUrl(updated);
          onChange?.(updated);
        });
        return updated;
      });
    },
    [updateUrl, onChange]
  );

  // Clear a single filter
  const clearFilter = useCallback(
    (key: keyof T) => {
      const config = schema[key as string];
      if (!config) return;

      const defaultValue = config.default ?? (config.type === 'array' ? [] : undefined);
      setFiltersState((prev) => {
        const newFilters = { ...prev, [key]: defaultValue };
        queueMicrotask(() => {
          updateUrl(newFilters);
          onChange?.(newFilters);
        });
        return newFilters;
      });
    },
    [schema, updateUrl, onChange]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFiltersState(defaults);
    queueMicrotask(() => {
      updateUrl(defaults);
      onChange?.(defaults);
    });
  }, [defaults, updateUrl, onChange]);

  // Sync with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlFilters = getFiltersFromUrl();
    if (JSON.stringify(urlFilters) !== JSON.stringify(filters)) {
      setFiltersState(urlFilters);
    }
  }, [searchParams]);

  // Cleanup debounce timers
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, []);

  // Compute active filters for display
  const activeFilters = useMemo((): ActiveFilter[] => {
    const active: ActiveFilter[] = [];

    for (const [key, config] of Object.entries(schema)) {
      const value = filters[key as keyof T];
      if (isDefaultValue(value, config.default, config.type)) continue;

      const label = (labels as Record<string, string>)[key] ?? key;
      const formatter = (formatters as Record<string, (v: unknown) => string>)[key];
      let displayValue: string;

      if (formatter) {
        displayValue = formatter(value);
      } else if (config.type === 'array' && Array.isArray(value)) {
        displayValue = value.join(', ');
      } else if (config.type === 'boolean') {
        displayValue = value ? 'Yes' : 'No';
      } else {
        displayValue = String(value ?? '');
      }

      active.push({ key, label, displayValue, value });
    }

    return active;
  }, [filters, schema, labels, formatters]);

  const hasActiveFilters = activeFilters.length > 0;

  return {
    filters,
    setFilter,
    setFilters,
    clearFilter,
    clearAllFilters,
    activeFilters,
    hasActiveFilters,
  };
}

export default useFilterParams;
